import { useCallback, useEffect, useRef } from 'react';
import { useSetRecoilState } from 'recoil';
import { Job, jobQueueExpandedState } from '~/state/atoms/jobAtoms';
import { WorkflowTypes } from '~/state/atoms/viewerAtoms';
import { useJobManagement } from '../useJobManagement';
import { downloadBackendResults, pollForResults, uploadFiles } from './utils';

/**
 * A hook for orchestrating backend jobs.
 * It processes jobs in the queue, handling file uploads, polling for results, and downloading final results.
 * @returns {void}
 */
export function useBackendJobOrchestrator() {
    const { jobQueue, setJobStatus, updateJob } = useJobManagement();
    const expandJobQueue = useSetRecoilState(jobQueueExpandedState);
    const currentlyProcessingJobIdsRef = useRef<Set<string>>(new Set());

    const processJob = useCallback(
        async (jobToProcess: Job) => {
            const { id: jobId, client, payload } = jobToProcess;
            if (!payload) {
                setJobStatus(jobId, 'Error', `Job payload is missing.`);
                return;
            }
            if (!client) {
                setJobStatus(
                    jobId,
                    'Error',
                    `Configuration error: No client for ${jobToProcess.type}.`
                );
                return;
            }

            try {
                setJobStatus(jobId, 'Uploading');
                await uploadFiles(
                    jobId,
                    client,
                    payload,
                    (successfullySubmittedCount) => {
                        updateJob(jobId, {
                            uploadedItems: successfullySubmittedCount,
                        });
                    }
                );

                if (jobToProcess.type === WorkflowTypes.DATA_UPLOAD) {
                    setJobStatus(jobId, 'Done');
                    return;
                }

                setJobStatus(jobId, 'Processing');
                const pollData = await pollForResults(jobId, client);

                setJobStatus(jobId, 'Downloading');
                const results = await downloadBackendResults(
                    jobId,
                    client,
                    pollData
                );
                if (results.errors.length > 0) {
                    setJobStatus(jobId, 'Error', results.errors.join(', '));
                } else {
                    updateJob(jobId, {
                        status: 'Done',
                        results: results.files,
                    });
                }
            } catch (error) {
                if (typeof error === 'string') {
                    setJobStatus(jobId, 'Error', error);
                } else if (error instanceof Error) {
                    setJobStatus(jobId, 'Error', error.message);
                } else {
                    setJobStatus(jobId, 'Error', 'Error processing task.');
                }
            }
        },
        [setJobStatus, updateJob]
    );

    useEffect(() => {
        const jobsToProcess = jobQueue.jobs.filter(
            (job) =>
                job.status === 'Queued' &&
                !currentlyProcessingJobIdsRef.current.has(job.id)
        );
        if (jobsToProcess.length > 0) {
            expandJobQueue(true);
        }
        jobsToProcess.forEach((job) => {
            currentlyProcessingJobIdsRef.current.add(job.id);
            processJob(job).finally(() => {
                currentlyProcessingJobIdsRef.current.delete(job.id);
            });
        });
    }, [jobQueue.jobs, processJob, setJobStatus]);
}
